import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { HotToastService } from '@ngneat/hot-toast';
import { Store } from '@ngrx/store';
import { forkJoin, Subscription } from 'rxjs';
import { concatMap } from 'rxjs/operators';

import { AddressFormData } from 'src/app/core/dtos/address-form-data';
import { CountryDto, UserOrderAddressDto } from 'src/app/core/dtos/dto';
import { HttpErrorResponseMessages } from 'src/app/core/dtos/http-error-response-messages';
import { Step } from 'src/app/core/dtos/step';
import { CheckoutService } from 'src/app/core/services/checkout.service';
import { CountryRegionService } from 'src/app/core/services/country-region.service';
import { ConfigHelper } from 'src/app/core/services/helpers/config.helper';
import { LoadingService } from 'src/app/core/services/loading.service';
import { CheckoutStepEnum, StepProgressService } from 'src/app/core/services/step-progress.service';
import { ModalComponent } from 'src/app/shared/components/modal/modal.component';
import { selectIsBusinessAccount } from 'src/app/state/my-account.selectors';

@Component({
  selector: 'app-address',
  templateUrl: './address.component.html',
  styleUrls: ['./address.component.scss']
})
export class AddressComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('checkoutBusinessAddressModal') checkoutBusinessAddressModal: ModalComponent;

  checkoutDisabled: boolean;
  maintenanceMessage: string;
  modalTitle: string;
  modalBody: string;
  isBusinessAccount: boolean;
  submitted: boolean;
  userOrderAddress: UserOrderAddressDto;
  billingAddressFormData: AddressFormData;
  shippingAddressFormData: AddressFormData;
  setAddressSubscription: Subscription;
  businessAccountSubscription: Subscription;
  countries: CountryDto[];
  addressInfo: UserOrderAddressDto;
  steps: Step[];

  constructor(
    private checkoutService: CheckoutService,
    private loadingService: LoadingService,
    private toastService: HotToastService,
    private store: Store,
    private router: Router,
    private countryService: CountryRegionService,
    private stepProgressService: StepProgressService,
    private configHelper: ConfigHelper,
  ) {
    this.steps = this.stepProgressService.getCheckoutSteps(CheckoutStepEnum.Address);
  }

  ngOnInit(): void {
    this.loadingService.show();
    this.getAddressInfo();
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.checkBusinessAccount();
    }, 0);
  }

  checkBusinessAccount() {
    this.businessAccountSubscription = this.store.select(selectIsBusinessAccount)
      .subscribe(
        result => {
          this.isBusinessAccount = result;
          if (this.isBusinessAccount) {
            const configureNotificationUrl = this.configHelper.getBaseUrl() + '/#/account/configure-notifications';
            this.modalTitle = "Important Note";
            this.modalBody = `Business users: Please check your <a href=${configureNotificationUrl} onclick="window.open(this.href,'_blank');return false;"> mailing settings </a> to ensure email and mail <u>(including score reports)</u > are sent to the correct addresses.</br>If you find that your settings were incorrect, please delete the registration from your Shopping Cart and re - add it`;
            this.checkoutBusinessAddressModal.open();
          }
        }
      );
  }

  getAddressInfo() {
    forkJoin([this.checkoutService.getCheckoutAddressInformation(), this.countryService.getCountries()])
      .subscribe(
        (result) => {
          if (result[0].content.maintenanceMessage) {
            this.maintenanceMessage = result[0].content.maintenanceMessage;
            this.checkoutDisabled = true;
          } else {
            this.userOrderAddress = result[0].content.userOrderAddress;
            this.checkoutDisabled = false;
            this.countries = result[1].content;
          }
          this.loadingService.hide();
        },
        (error: HttpErrorResponse) => {
          this.loadingService.hide();
          if (error.status !== 401) {
            this.toastService.warning("Unable to load page");
          }
        }
      );
  }

  billingFormValueChanged(formData: AddressFormData) {
    this.billingAddressFormData = formData;
  }

  shippingFormValueChanged(formData: AddressFormData) {
    this.shippingAddressFormData = formData;
  }

  onNextClick() {
    this.submitted = true;
    // check if the forms are valid
    const isValid = this.validateAddressForms();
    if (isValid) {
      this.prepareAddressInfo();
      if (this.addressInfo) {
        this.loadingService.show();
        this.setAddressSubscription = this.checkoutService.setCheckoutAddressInformation(this.addressInfo)
          .pipe(concatMap(() => this.checkoutService.prepareCartForShipping()),
        ).subscribe(
          () => {
            this.loadingService.hide();
            this.router.navigate(['checkout/shipping']);
          },
          (error: HttpErrorResponse) => {
            this.loadingService.hide();
            if (error.status !== 401) {
              const errorMessage = error.error as HttpErrorResponseMessages;
              if (errorMessage?.reasons) {
                errorMessage.reasons.forEach(reasons => {
                  this.toastService.warning(reasons.errorMessage);
                });
              }
              else {
                this.toastService.warning("Failed to save address information because of unknown problem. Please contact Paragon.");
              }
            }
          });
      }
    }
    else {
      this.toastService.warning("Please fill in the missing fields");
      this.addressInfo = null;
    }

  }

  validateAddressForms(): boolean {
    if (this.userOrderAddress.needShippingAddress) {
      return (this.shippingAddressFormData?.isValid && (this.userOrderAddress.areShippingBillingAddressSame || this.billingAddressFormData?.isValid));
    } else {
      return this.billingAddressFormData?.isValid;
    }
  }

  prepareAddressInfo() {
    const needShippingAddress = this.userOrderAddress.needShippingAddress;
    const areShippingBillingAddressSame = this.userOrderAddress.areShippingBillingAddressSame;
    let shippingAddress = this.shippingAddressFormData?.address ? this.shippingAddressFormData.address : this.userOrderAddress.shippingAddress;
    const billingAddress = this.billingAddressFormData?.address ? this.billingAddressFormData.address : this.userOrderAddress.billingAddress;

    if (this.userOrderAddress.needShippingAddress) {
      this.addressInfo =
      {
        needShippingAddress,
        shippingAddress,
        billingAddress: areShippingBillingAddressSame ? shippingAddress : billingAddress,
        areShippingBillingAddressSame
      };
    }
    else {
      shippingAddress = this.shippingAddressFormData?.isValid ? shippingAddress : billingAddress;
      this.addressInfo =
      {
        needShippingAddress,
        shippingAddress,
        billingAddress,
        areShippingBillingAddressSame
      };
    }

  }

  ngOnDestroy(): void {
    this.setAddressSubscription?.unsubscribe();
    this.businessAccountSubscription?.unsubscribe();
  }

}
